/** Страница «Компания» и привязка KPI: дерево подразделений, люди, подбор сотрудника по ФИО. [Claude] 2026-11-03, Чат 6, №35. */

export const COMPANY_BLOCKS = [
    { key: "summary", label: "Сводка" },
    { key: "people", label: "Сотрудники" },
    { key: "kpi", label: "KPI" },
    { key: "units", label: "Подразделения" },
];

export const DEFAULT_COMPANY_PREFS = {
    blocks: COMPANY_BLOCKS.map((b) => b.key),
    hidden: [],
    view: "tree",
    include_children: true,
    last_unit: null,
};

/** Дети по родителю, отсортированные как на схеме. */
export function childrenMap(units) {
    const map = new Map();
    (units || []).forEach((u) => {
        const key = u.parent_id ?? null;
        if (!map.has(key)) map.set(key, []);
        map.get(key).push(u);
    });
    map.forEach((list) => list.sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0) || String(a.name).localeCompare(String(b.name), "ru")));
    return map;
}

/** Корни: без родителя или родитель не виден (архив). */
export function rootUnits(units) {
    const ids = new Set((units || []).map((u) => u.id));
    return (units || []).filter((u) => u.parent_id == null || !ids.has(u.parent_id))
        .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
}

/** Подразделение и все дочерние (id). */
export function descendantIds(unitId, units) {
    const map = childrenMap(units);
    const out = new Set();
    const stack = [unitId];
    while (stack.length) {
        const id = stack.pop();
        if (id == null || out.has(id)) continue;
        out.add(id);
        (map.get(id) || []).forEach((c) => stack.push(c.id));
    }
    return out;
}

/** Путь от корня до подразделения (для «хлебных крошек»). */
export function unitPath(unitId, units) {
    const byId = new Map((units || []).map((u) => [u.id, u]));
    const path = [];
    let cur = byId.get(unitId);
    const seen = new Set();
    while (cur && !seen.has(cur.id)) {
        seen.add(cur.id);
        path.unshift(cur);
        cur = byId.get(cur.parent_id);
    }
    return path;
}

const ROLE_ORDER = { head: 0, deputy: 1, member: 2 };

/** Люди подразделения (или с дочерними): [{member, employee, unit}], руководители первыми, без повторов. */
export function unitPeople(unitIds, members, employees, units) {
    const ids = unitIds instanceof Set ? unitIds : new Set(unitIds);
    const emp = new Map((employees || []).map((e) => [e.id, e]));
    const unit = new Map((units || []).map((u) => [u.id, u]));
    const best = new Map();
    (members || []).filter((m) => ids.has(m.unit_id) && emp.has(m.employee_id)).forEach((m) => {
        const prev = best.get(m.employee_id);
        if (!prev || (ROLE_ORDER[m.role] ?? 9) < (ROLE_ORDER[prev.member.role] ?? 9)) {
            best.set(m.employee_id, { member: m, employee: emp.get(m.employee_id), unit: unit.get(m.unit_id) });
        }
    });
    return [...best.values()].sort((a, b) => (ROLE_ORDER[a.member.role] ?? 9) - (ROLE_ORDER[b.member.role] ?? 9)
        || String(a.employee.full_name).localeCompare(String(b.employee.full_name), "ru"));
}

/** Названия ролей: backend отдаёт словарь или список. */
export function roleLabel(roles, key) {
    if (Array.isArray(roles)) {
        const r = roles.find((x) => x.key === key || x.value === key);
        return r?.label || key;
    }
    if (roles && typeof roles === "object") return roles[key]?.label || roles[key] || key;
    return { head: "Руководитель", deputy: "Заместитель", member: "Сотрудник" }[key] || key;
}

/** Привести сохранённые настройки к полному виду. */
export function normalizeCompanyPrefs(raw) {
    const keys = COMPANY_BLOCKS.map((b) => b.key);
    const p = raw && typeof raw === "object" ? raw : {};
    const blocks = [...new Set((Array.isArray(p.blocks) ? p.blocks : []).filter((k) => keys.includes(k)))];
    keys.forEach((k) => { if (!blocks.includes(k)) blocks.push(k); });
    return {
        blocks,
        hidden: [...new Set((Array.isArray(p.hidden) ? p.hidden : []).filter((k) => keys.includes(k)))],
        view: p.view === "chart" ? "chart" : "tree",
        include_children: typeof p.include_children === "boolean" ? p.include_children : true,
        last_unit: Number.isInteger(p.last_unit) && p.last_unit > 0 ? p.last_unit : null,
    };
}

export function moveBlock(blocks, key, delta) {
    const list = [...blocks];
    const i = list.indexOf(key);
    const j = i + delta;
    if (i < 0 || j < 0 || j >= list.length) return list;
    [list[i], list[j]] = [list[j], list[i]];
    return list;
}

const norm = (s) => String(s || "").toLowerCase().replace(/ё/g, "е").replace(/[^a-zа-я0-9\s]/g, " ").split(/\s+/).filter(Boolean);

/**
 * Подобрать сотрудника по ФИО из карты КПЭ («Иванов Иван», «Иванов И.», «Иван Иванов»).
 * Возвращает сотрудника, только если совпадение однозначное, иначе null.
 */
export function matchEmployee(name, employees) {
    const words = norm(name);
    if (!words.length) return null;
    const scored = (employees || []).map((e) => {
        const ew = norm(e.full_name);
        let score = 0;
        words.forEach((w) => {
            if (ew.includes(w)) score += 2;
            else if (w.length === 1 && ew.some((x) => x.startsWith(w))) score += 1;
        });
        return { e, score, full: words.every((w) => ew.includes(w) || (w.length === 1 && ew.some((x) => x.startsWith(w)))) };
    }).filter((x) => x.full && x.score >= 3);
    if (!scored.length) return null;
    scored.sort((a, b) => b.score - a.score);
    if (scored.length > 1 && scored[1].score === scored[0].score) return null;
    return scored[0].e;
}
